import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserForm from '../components/UserForm';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import api from '../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return false;
    }
    return token;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = checkAuth();
      if (!token) return;

      const response = await api.get('/users/');
      
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      } else if (error.response?.status === 403) {
        setNotification({
          open: true,
          message: 'Você não tem permissão para acessar esta página',
          severity: 'error'
        });
        setTimeout(() => navigate('/projects'), 2000);
        return;
      }
      setNotification({
        open: true,
        message: 'Erro ao carregar usuários',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = checkAuth();
      if (!token) return;

      const response = await api.get('/roles/');

      setRoles(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar roles:', error);
      setRoles([]); // Ensure roles is always an array
    }
  };

  const fetchResponsibles = async () => {
    try {
      const token = checkAuth();
      if (!token) return;

      const response = await api.get('/technical-responsibles/');

      const data = response.data || [];
      setResponsibles(Array.isArray(data) ? data.filter(r => r.is_active) : []);
    } catch (error) {
      console.error('Erro ao buscar responsáveis técnicos:', error);
      setResponsibles([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchResponsibles();
  }, []);

  const handleCreateUser = async (userData) => {
    try {
      const token = checkAuth();
      if (!token) return;

      await api.post('/users/', userData);

      setOpenCreateForm(false);
      setNotification({
        open: true,
        message: 'Usuário criado com sucesso!',
        severity: 'success'
      });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setNotification({
        open: true,
        message: `Erro ao criar usuário: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleEditUser = async (userData) => {
    try {
      const token = checkAuth();
      if (!token) return;

      await api.put(`/users/${selectedUser.user_id}`, userData);

      setOpenEditForm(false);
      setNotification({
        open: true,
        message: 'Usuário atualizado com sucesso!',
        severity: 'success'
      });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      setNotification({
        open: true,
        message: `Erro ao atualizar usuário: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      const token = checkAuth();
      if (!token) return;

      await api.delete(`/users/${selectedUser.user_id}`);

      setOpenDeleteDialog(false);
      setNotification({
        open: true,
        message: 'Usuário excluído com sucesso!',
        severity: 'success'
      });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      setNotification({
        open: true,
        message: `Erro ao excluir usuário: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
      setOpenDeleteDialog(false);
    }
  };

  const handleLinkUserToResponsible = async () => {
    try {
      await api.post(`/users/${selectedUser.user_id}/link-responsible/${selectedResponsible}`, {});
      
      fetchUsers();
      
      setOpenLinkDialog(false);
      setSelectedUser(null);
      setSelectedResponsible(null);
      
      setNotification({
        open: true,
        message: 'Usuário associado ao responsável técnico com sucesso',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao associar usuário a responsável técnico:', error);
      setNotification({
        open: true,
        message: error.response?.data?.detail || 'Erro ao associar usuário a responsável técnico',
        severity: 'error'
      });
    }
  };

  const handleUnlinkUserFromResponsible = async (userId) => {
    try {
      await api.post(`/users/${userId}/unlink-responsible`, {});
      
      fetchUsers();
      
      setNotification({
        open: true,
        message: 'Usuário desassociado do responsável técnico com sucesso',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao desassociar usuário de responsável técnico:', error);
      setNotification({
        open: true,
        message: error.response?.data?.detail || 'Erro ao desassociar usuário de responsável técnico',
        severity: 'error'
      });
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.role_id === roleId);
    return role ? role.role_name : 'Desconhecido';
  };

  const getRoleColor = (roleName) => {
    switch (roleName.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'editor':
        return 'primary';
      case 'user':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString;
    }
  };

  const getResponsibleName = (responsibleId) => {
    if (!responsibleId) return '-';
    const responsible = responsibles.find(r => r.responsible_id === responsibleId);
    return responsible ? responsible.full_name : `ID: ${responsibleId}`;
  };

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, md: 2 }, mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gerenciamento de Usuários
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateForm(true)}
        >
          Novo Usuário
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>Nenhum usuário encontrado</Typography>
          <Typography variant="body1" color="textSecondary" mb={3}>
            Clique em "Novo Usuário" para adicionar um usuário
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCreateForm(true)}
            startIcon={<AddIcon />}
          >
            Novo Usuário
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nome de Usuário</TableCell>
                <TableCell>Perfil</TableCell>
                <TableCell>Responsável Técnico</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell>Último Login</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>{user.user_id}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleName(user.role_id)}
                      color={getRoleColor(getRoleName(user.role_id))}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {getResponsibleName(user.responsible_id)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Ativo' : 'Inativo'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>{formatDate(user.last_login)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Editar">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setSelectedUser(user);
                            setOpenEditForm(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={user.responsible_id ? "Desassociar do Responsável" : "Associar a Responsável"}>
                        <IconButton
                          color={user.responsible_id ? "warning" : "primary"}
                          onClick={() => {
                            if (user.responsible_id) {
                              // Confirmar antes de desassociar
                              if (window.confirm(`Deseja desassociar ${user.username} do responsável técnico?`)) {
                                handleUnlinkUserFromResponsible(user.user_id);
                              }
                            } else {
                              setSelectedUser(user);
                              setOpenLinkDialog(true);
                            }
                          }}
                        >
                          {user.responsible_id ? <LinkOffIcon /> : <LinkIcon />}
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Excluir">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedUser(user);
                            setOpenDeleteDialog(true);
                          }}
                          disabled={user.user_id === 1} // Impedir exclusão do admin
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Formulário de criação de usuário */}
      <UserForm
        open={openCreateForm}
        onClose={() => setOpenCreateForm(false)}
        onSubmit={handleCreateUser}
        roles={roles}
        isEditing={false}
      />

      {/* Formulário de edição de usuário */}
      {selectedUser && (
        <UserForm
          open={openEditForm}
          onClose={() => setOpenEditForm(false)}
          onSubmit={handleEditUser}
          initialData={selectedUser}
          roles={roles}
          isEditing={true}
        />
      )}

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o usuário {selectedUser?.username}? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para associar usuário a responsável técnico */}
      <Dialog open={openLinkDialog} onClose={() => setOpenLinkDialog(false)}>
        <DialogTitle>Associar a Responsável Técnico</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Selecione o responsável técnico para associar ao usuário <strong>{selectedUser?.username}</strong>.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="responsible-select-label">Responsável Técnico</InputLabel>
            <Select
              labelId="responsible-select-label"
              value={selectedResponsible || ''}
              onChange={(e) => setSelectedResponsible(e.target.value)}
              label="Responsável Técnico"
            >
              {Array.isArray(responsibles) ? responsibles.map((responsible) => (
                <MenuItem key={responsible.responsible_id} value={responsible.responsible_id}>
                  {responsible.full_name} - {responsible.department || 'Sem departamento'}
                </MenuItem>
              )) : <MenuItem value="">Nenhum responsável disponível</MenuItem>}
            </Select>
            <FormHelperText>
              O usuário terá o papel definido como "tecnico" automaticamente
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLinkDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleLinkUserToResponsible} 
            color="primary"
            disabled={!selectedResponsible}
          >
            Associar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Users; 